/**
 * Vendor Bot Admin - Dashboard
 * Sales summary, recent orders, quick stats
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import StatsCard from '../../components/StatsCard';
import OrderCard from '../../components/OrderCard';
import Button from '../../components/Button';
import { useTelegram } from '../../hooks/useTelegram';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
`;

const SectionTitle = styled.h3`
  font-size: var(--font-lg);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
  margin: var(--spacing-lg) 0 var(--spacing-md);
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const AdminDashboard: React.FC = () => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const navigate = useNavigate();
  const { showMainButton, showBackButton, close } = useTelegram();
  const [recentOrders] = useState([
    {
      orderId: '12345',
      date: 'Oct 6, 2025',
      status: 'completed' as const,
      items: ['Product A', 'Product B'],
      total: '$49.99',
    },
    {
      orderId: '12344',
      date: 'Oct 5, 2025',
      status: 'pending' as const,
      items: ['Product C', 'Product D', 'Product E'],
      total: '$129.99',
    },
  ]);

  useEffect(() => {
    showMainButton('Add Product', () => navigate(`/vendor/${botUsername}/admin/product/new`));
    showBackButton(() => close());
  }, [showMainButton, showBackButton, close, navigate, botUsername]);

  return (
    <Layout header={<Navbar title={`Admin Panel`} subtitle={botUsername || 'Store Management'} />}>
      <StatsGrid>
        <StatsCard
          label="Today's Sales"
          value="$245.50"
          icon="💰"
          color="var(--accent-green)"
          change={{ value: '12%', positive: true }}
        />
        <StatsCard
          label="Orders"
          value="8"
          icon="📦"
          change={{ value: '3 pending', positive: true }}
        />
        <StatsCard label="Products" value="45" icon="📱" />
        <StatsCard label="Customers" value="127" icon="👥" change={{ value: '5 new', positive: true }} />
      </StatsGrid>

      <SectionTitle>Quick Actions</SectionTitle>
      <QuickActions>
        <Button variant="primary" onClick={() => navigate(`/vendor/${botUsername}/admin/products`)}>
          📱 Products
        </Button>
        <Button variant="secondary" onClick={() => navigate(`/vendor/${botUsername}/admin/orders`)}>
          📦 Orders
        </Button>
        <Button variant="secondary" onClick={() => navigate(`/vendor/${botUsername}/admin/customers`)}>
          👥 Customers
        </Button>
        <Button variant="secondary" onClick={() => navigate(`/vendor/${botUsername}/admin/analytics`)}>
          📊 Analytics
        </Button>
      </QuickActions>

      <SectionTitle>Recent Orders</SectionTitle>
      <OrdersList>
        {recentOrders.map((order) => (
          <OrderCard
            key={order.orderId}
            {...order}
            onClick={() => navigate(`/vendor/${botUsername}/admin/order/${order.orderId}`)}
          />
        ))}
      </OrdersList>

      <Button
        fullWidth
        variant="outline"
        onClick={() => navigate(`/vendor/${botUsername}/admin/orders`)}
        style={{ marginTop: 'var(--spacing-md)' }}
      >
        View All Orders
      </Button>
    </Layout>
  );
};

export default AdminDashboard;
